import { useQuery } from "@tanstack/react-query"

import type { QueryFunctionContext } from "@tanstack/react-query"
import type { Assignment } from "@/types/djangio"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

export const assignmentsKeys = {
    all: [{ _model: "assignments" }] as const,
    detail: ({
        entityId,
        assignmentType,
    }: {
        entityId: string
        assignmentType: {
            qdt_field: string
            value: number
        }
    }) => [{ ...assignmentsKeys.all[0], entityId, assignmentType }] as const,
}

const fetchAssignments = async ({
    queryKey,
}: QueryFunctionContext<ReturnType<(typeof assignmentsKeys)["detail"]>>): Promise<{ objects: Assignment[] }> => {
    const [{ entityId, assignmentType }] = queryKey

    const response = await DjangIO.app.userdata.models.Assignment.objects
        .filter({
            assignment_type: assignmentType.value,
            [assignmentType.qdt_field]: entityId,
            estimated_count: true,
            dehydrate_extra: ["related_objects"],
        })
        .GET()

    return response
}

const selectAssignment = (queryResultData: Awaited<ReturnType<typeof fetchAssignments>>) => {
    const getAssignmentsByType = (assignmentTypeField: keyof Assignment["_extra"]["related_objects"]) =>
        queryResultData.objects
            .filter((assignment) => assignment._extra.related_objects[assignmentTypeField])
            .map((assignment) => ({
                assignmentId: assignment.id,
                recipientId: assignment._extra.related_objects[assignmentTypeField] as number,
            }))

    const assignmentsSupportersObjects = getAssignmentsByType("recipient_supporter_id")
    const assignmentsSupporters = assignmentsSupportersObjects.map((assignment) => assignment.recipientId)

    const assignmentsTeamMembersObjects = getAssignmentsByType("recipient_id")
    const assignmentsTeamMembers = assignmentsTeamMembersObjects.map((assignment) => assignment.recipientId)

    return {
        assignmentsSupportersObjects,
        assignmentsSupporters,
        assignmentsTeamMembersObjects,
        assignmentsTeamMembers,
    }
}

export const useAssignments = (
    entityId: string,
    assignmentType: {
        qdt_field: string
        value: number
    },
) =>
    useQuery({
        queryKey: assignmentsKeys.detail({ entityId, assignmentType }),
        queryFn: fetchAssignments,
        enabled: Boolean(entityId),
        select: selectAssignment,
    })

// Context on this function here: shared/profiles/assignment/action-creators.js
const getAssignmentAddedDeletedIds = (initialAssignmentObjects, finalRecipientIds) => {
    // These are either Supporter IDs or Team Member IDs
    const addedRecipientIds = []
    // These are IDs of Assignments (and not Supporter IDs or Team Member IDs)
    const deletedAssignmentIds = []

    initialAssignmentObjects.forEach((initialAssignment) => {
        if (!finalRecipientIds.includes(initialAssignment.recipientId)) {
            // assignment IDs
            deletedAssignmentIds.push(initialAssignment.assignmentId)
        }
    })

    const initialRecipientIds = initialAssignmentObjects.map((assignment) => assignment.recipientId)

    finalRecipientIds.forEach((selectedId) => {
        if (!initialRecipientIds.includes(selectedId)) {
            // recipient IDs
            addedRecipientIds.push(selectedId)
        }
    })

    return [addedRecipientIds, deletedAssignmentIds]
}

// shared/profiles/assignment/action-creators.js
export const updateAssignments = async ({
    values,
    assignments,
    entityId,
    // These vary based on Entity (ex. Supporter or Public Organization)
    assignmentType,
    quorumDataTypeEnum,
}) => {
    const assignmentsTeamMembersObjectsInitial =
        (assignments.data && assignments.data.assignmentsTeamMembersObjects) || []
    const assignmentsTeamMembersIdsFinal = values.assignmentsTeamMembers
    const assignmentsSupportersObjectsInitial =
        (assignments.data && assignments.data.assignmentsSupportersObjects) || []
    const assignmentsSupportersIdsFinal = values.assignmentsSupporters

    // Find the difference between Final and Initial Team Member IDs and determine
    // which Team Member IDs to add and which Assignment IDs to delete
    const [addedUserIds, deletedUserAssignmentIds] = getAssignmentAddedDeletedIds(
        assignmentsTeamMembersObjectsInitial,
        assignmentsTeamMembersIdsFinal,
    )

    const [addedSupporterIds, deletedSupporterAssignmentIds] = getAssignmentAddedDeletedIds(
        assignmentsSupportersObjectsInitial,
        assignmentsSupportersIdsFinal,
    )

    // Create/update selected Team Member Assignments
    if (Boolean(addedUserIds.length)) {
        const addedAssignmentsTeamMembersObjects = addedUserIds.map((teamMembersRecipientId) => {
            return {
                assignment_type: assignmentType.value,
                creator: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                recipient: DjangIO.app.userdata.models.QuorumUser.foreignKey(teamMembersRecipientId),
                // entity qdt field: resource url of newly created entity (ex. aka a Supporter for the create Contact form)
                // supporter: "api/newsupporter/XXXXXX"
                [assignmentType.qdt_field]: DjangIO.newResourceFromDataType(quorumDataTypeEnum.value).foreignKey(
                    entityId,
                ),
            }
        })

        const assignmentsTeamMembersResponse = await DjangIO.app.userdata.models.Assignment.objects
            .bulk_update({ objects: addedAssignmentsTeamMembersObjects })
            .filter({ dehydrate_extra: ["related_objects"] })
            // Even though this is a patch, it will post new assignments if they don't already exist
            .PATCH()

        if (assignmentsTeamMembersResponse) {
            // shared/user-actions/user-actions-action-creators.js
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.create_assignment,
                quorumDataType: quorumDataTypeEnum.value,
                description: DjangIO.app.userdata.types.AssignmentType.items().find(
                    (x) => x.quorum_data_type === quorumDataTypeEnum.value,
                ).label,
                regions: [null],
            })
        }
    }

    // "Delete" unselected initial Team Member Assignments
    if (Boolean(deletedUserAssignmentIds.length)) {
        deletedUserAssignmentIds.map((deletedAssignmentId) =>
            DjangIO.app.userdata.models.Assignment.objects
                .get(deletedAssignmentId)
                .update({
                    archived: true,
                })
                .filter({ dehydrate_extra: ["related_objects"] })
                .PATCH()
                .then(() => {
                    sendUserActionPost({
                        userActionType: DjangIO.app.useractions.types.UserActionType.remove_assignment,
                        quorumDataType: quorumDataTypeEnum.value,
                        description: DjangIO.app.userdata.types.AssignmentType.items().find(
                            (x) => x.quorum_data_type === quorumDataTypeEnum.value,
                        ).label,
                        regions: [null],
                    })
                }),
        )
    }

    // Create/update selected Supporter Assignments
    if (Boolean(addedSupporterIds.length)) {
        const addedAssignmentsSupportersObjects = addedSupporterIds.map((supportersRecipientId) => {
            return {
                assignment_type: assignmentType.value,
                creator: DjangIO.app.userdata.models.QuorumUser.foreignKey(Userdata.id),
                recipient_supporter: DjangIO.app.grassroots.models.Supporter.foreignKey(supportersRecipientId),
                [assignmentType.qdt_field]: DjangIO.newResourceFromDataType(quorumDataTypeEnum.value).foreignKey(
                    entityId,
                ),
            }
        })

        const assignmentsSupportersResponse = await DjangIO.app.userdata.models.Assignment.objects
            .bulk_update({ objects: addedAssignmentsSupportersObjects })
            .filter({ dehydrate_extra: ["related_objects"] })
            // Even though this is a patch, it will post new assignments if they don't already exist
            .PATCH()

        if (assignmentsSupportersResponse) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.create_assignment,
                quorumDataType: quorumDataTypeEnum.value,
                description: DjangIO.app.userdata.types.AssignmentType.items().find(
                    (x) => x.quorum_data_type === quorumDataTypeEnum.value,
                ).label,
                regions: [null],
            })
        }
    }

    // "Delete" unselected initial Supporter Assignments
    if (Boolean(deletedSupporterAssignmentIds.length)) {
        deletedSupporterAssignmentIds.map((deletedAssignmentId) =>
            DjangIO.app.userdata.models.Assignment.objects
                .get(deletedAssignmentId)
                .update({
                    archived: true,
                })
                .filter({ dehydrate_extra: ["related_objects"] })
                .PATCH()
                .then(() => {
                    sendUserActionPost({
                        userActionType: DjangIO.app.useractions.types.UserActionType.remove_assignment,
                        quorumDataType: quorumDataTypeEnum.value,
                        description: DjangIO.app.userdata.types.AssignmentType.items().find(
                            (x) => x.quorum_data_type === quorumDataTypeEnum.value,
                        ).label,
                        regions: [null],
                    })
                }),
        )
    }
}
